import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
// RM2/RM5 (register RM-CAN-02/05): the board registry through the local
// server's grant door. The desk's launchers, deck titles, and workroom
// births derive from the World's registry rows with the person's entitlement
// fold (BoardEntitledRow) — never from a list held in this app. One read per
// World is remembered as a DISPLAY snapshot (looking writes nothing; the rows
// are the authority's) so synchronous seams — a title for a tab, a reserved
// title check — read the same rows the launchers rendered.
import { BoardRegistryReadback, type BoardEntitledRow } from "@cleo/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { useEffect, useSyncExternalStore } from "react";
import { useNeucleosWorldStore } from "./worldStore";

export type BoardRegistryOutcome =
  | { readonly ok: true; readonly registry: BoardRegistryReadback }
  | { readonly ok: false; readonly kind: string; readonly reason: string };

const decodeBoardRegistry = Schema.decodeUnknownOption(BoardRegistryReadback);

export async function readWorldBoards(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<BoardRegistryOutcome> {
  let response: Response;
  try {
    response = await postLocalJson("/api/neucleos/world/boards", { worldKey }, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) {
    return {
      ok: false,
      kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
      reason:
        typeof body?.["reason"] === "string"
          ? body["reason"]
          : "The World's board registry is unavailable.",
    };
  }
  const registry = Option.getOrUndefined(decodeBoardRegistry(body["registry"]));
  if (registry === undefined || registry.worldKey !== worldKey) {
    return {
      ok: false,
      kind: "transport",
      reason: "The local Cleo server returned an invalid board registry.",
    };
  }
  return { ok: true, registry };
}

// ---- the per-World display snapshot -------------------------------------

interface RegistryRead {
  readonly grantRevision: number;
  readonly worldKey: string;
  readonly outcome: BoardRegistryOutcome | null;
}

const reads = new Map<string, RegistryRead>();
const listeners = new Set<() => void>();
let inflight = new Map<string, Promise<void>>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** The last registry read for a World, or null before one has answered. */
export function boardRegistrySnapshot(worldKey: string): BoardRegistryReadback | null {
  const read = reads.get(worldKey);
  return read?.grantRevision === useNeucleosWorldStore.getState().grantRevision &&
    read.outcome?.ok === true
    ? read.outcome.registry
    : null;
}

/** Seed the snapshot from a known readback (tests and stories only). */
export function seedBoardRegistrySnapshot(registry: BoardRegistryReadback): void {
  reads.set(registry.worldKey, {
    worldKey: registry.worldKey,
    grantRevision: useNeucleosWorldStore.getState().grantRevision,
    outcome: { ok: true, registry },
  });
  emit();
}

export function resetBoardRegistrySnapshots(): void {
  reads.clear();
  inflight = new Map();
  emit();
}

export function ensureBoardRegistryRead(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  const grantRevision = useNeucleosWorldStore.getState().grantRevision;
  const key = `${worldKey}\u0000${grantRevision}`;
  const pending = inflight.get(key);
  if (pending !== undefined) return pending;
  const task = readWorldBoards(worldKey, fetchImpl).then((outcome) => {
    if (inflight.get(key) !== task) return;
    inflight.delete(key);
    if (useNeucleosWorldStore.getState().grantRevision !== grantRevision) return;
    reads.set(worldKey, { worldKey, grantRevision, outcome });
    emit();
  });
  inflight.set(key, task);
  // A routine refresh must not remove the open board and discard its form.
  // Keep the verified display for this same grant until the read answers.
  // Grant replacement and completed refusals still withdraw it immediately;
  // every operation remains authorized independently by its server door.
  const previous = reads.get(worldKey);
  if (
    previous === undefined ||
    previous.grantRevision !== grantRevision ||
    previous.outcome?.ok !== true
  ) {
    reads.set(worldKey, { worldKey, grantRevision, outcome: null });
    emit();
  }
  return task;
}

/** A confirmed change needs a read started after it, not an older in-flight snapshot. */
export async function refreshBoardRegistryAfterChange(worldKey: string): Promise<void> {
  const grantRevision = useNeucleosWorldStore.getState().grantRevision;
  const pending = inflight.get(`${worldKey}\u0000${grantRevision}`);
  if (pending !== undefined) await pending;
  if (useNeucleosWorldStore.getState().grantRevision !== grantRevision) return;
  await ensureBoardRegistryRead(worldKey);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export interface WorldBoardsState {
  readonly registry: BoardRegistryReadback | null;
  readonly loading: boolean;
  readonly refusal: { readonly kind: string; readonly reason: string } | null;
}

const NO_WORLD: WorldBoardsState = { registry: null, loading: false, refusal: null };
const LOADING: WorldBoardsState = { registry: null, loading: true, refusal: null };
const stateCache = new Map<string, WorldBoardsState>();

function stateFor(worldKey: string): WorldBoardsState {
  const read = reads.get(worldKey);
  if (
    read === undefined ||
    read.outcome === null ||
    read.grantRevision !== useNeucleosWorldStore.getState().grantRevision
  )
    return LOADING;
  const cached = stateCache.get(worldKey);
  if (
    cached !== undefined &&
    cached.registry === (read.outcome.ok ? read.outcome.registry : null) &&
    cached.refusal?.reason === (read.outcome.ok ? undefined : read.outcome.reason)
  ) {
    return cached;
  }
  const next: WorldBoardsState = read.outcome.ok
    ? { registry: read.outcome.registry, loading: false, refusal: null }
    : {
        registry: null,
        loading: false,
        refusal: { kind: read.outcome.kind, reason: read.outcome.reason },
      };
  stateCache.set(worldKey, next);
  return next;
}

/** The World's board registry, live: refusal is data, loading is named. */
export function useWorldBoards(worldKey: string | null): WorldBoardsState {
  const grantRevision = useNeucleosWorldStore((state) => state.grantRevision);
  useEffect(() => {
    if (worldKey === null) return;
    if (reads.get(worldKey) === undefined || reads.get(worldKey)?.grantRevision !== grantRevision)
      void ensureBoardRegistryRead(worldKey);
    // HTTP snapshots have no cross-host push. Returning rechecks authority
    // without destroying an open form while the same-grant read is pending.
    const refresh = () => {
      void ensureBoardRegistryRead(worldKey);
    };
    const visible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [worldKey, grantRevision]);
  return useSyncExternalStore(
    subscribe,
    () => (worldKey === null ? NO_WORLD : stateFor(worldKey)),
    () => (worldKey === null ? NO_WORLD : stateFor(worldKey)),
  );
}

/** The World's registry snapshot, live, WITHOUT starting a read: seats that
 * stand beside the launchers (which already read for the World) render from
 * the same rows and never fire a second door call of their own. */
export function useBoardRegistrySnapshot(worldKey: string | null): BoardRegistryReadback | null {
  return useSyncExternalStore(
    subscribe,
    () => (worldKey === null ? null : boardRegistrySnapshot(worldKey)),
    () => (worldKey === null ? null : boardRegistrySnapshot(worldKey)),
  );
}

/** One registry row by its key, from the World's snapshot. */
export function boardRow(worldKey: string, key: string): BoardEntitledRow | undefined {
  return boardRegistrySnapshot(worldKey)?.boards.find((row) => row.key === key);
}

/** Every board title in the World's snapshot — the reserved session labels. */
export function boardTitles(worldKey: string): ReadonlyArray<string> {
  return boardRegistrySnapshot(worldKey)?.boards.map((row) => row.title) ?? [];
}
